// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
// Material
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';

// RxJS
import { tap } from 'rxjs/operators';
import {
  PatientWeightModel,
  PatientWeightOnServerCreated,
  PatientWeightsDataSource,
  PatientWeightsPageRequested,
  selectPatientWeightErrorState,
  selectPatientWeightsActionLoading,
} from 'src/app/core/lists';
import { merge, Observable, Subscription } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
  TypesUtilsService,
} from '../../../../../../../core/_base/crud';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../../core/reducers';
import { DatePipe } from '@angular/common';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM-DD-YY',
  },
  display: {
    dateInput: 'MM-DD-YY',
    monthYearLabel: 'MMM, YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@Component({
  selector: 'kt-patient-weight',
  templateUrl: './patient-weight.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class PatientWeightComponent implements OnInit {
  // Public properties
  // Incoming data
  @Input() patientId$: Observable<number>;
  patientId: number;
  // Table fields
  dataSource: PatientWeightsDataSource;
  displayedColumns = ['weight', 'start_date'];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  // Filter fields
  //@ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  filterStatus = '';
  filterHoldHospital = '';
  filterType = '';
  isSwitchedToEditMode = false;
  loadingAfterSubmit = false;
  formGroup: FormGroup;
  weightForEdit: PatientWeightModel;
  weightForAdd: PatientWeightModel;
  submissionErrorObj: any;
  hasSubmissionErrors = false;
  // Selection
  selection = new SelectionModel<PatientWeightModel>(true, []);
  patientWeightsResult: PatientWeightModel[] = [];
  // Subscriptions
  private subscriptions: any[] = [];
  private componentSubscriptions: Subscription;
  measurementSystem = localStorage.getItem('measurementSystem');

  /**
   * Component constructor
   *
   * @param dialogRef
   * @param data
   * @param snackBar
   * @param layoutUtilsService
   * @param translate
   * @param store
   * @param cdr
   * @param fb
   * @param typesUtilsService
   * @param datePipe
   */

  constructor(
    public dialogRef: MatDialogRef<PatientWeightComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public snackBar: MatSnackBar,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    public typesUtilsService: TypesUtilsService,
    private datePipe: DatePipe,
  ) {
    this.store.subscribe();
    this.weightForAdd = new PatientWeightModel();
  }

  /**
   * On init
   */
  ngOnInit(): void {
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    /* Data load will be triggered in two cases:
		- when a pagination event occurs => this.paginator.page
		- when a sort event occurs => this.sort.sortChange
		**/
    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadPatientWeightsList()))
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });
    // Init DataSource
    this.dataSource = new PatientWeightsDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject.subscribe(
      (res) => (this.patientWeightsResult = res),
    );
    this.subscriptions.push({ id: 3, sub: entitiesSubscription });

    this.patientId = this.data.id;
    this.loadPatientWeightsList();
    this.createFormGroup();
  }

  /**
   * Load Patients List from service through data-sources
   */
  loadPatientWeightsList() {
    this.selection.clear();
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction,
      this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server
    this.store.dispatch(new PatientWeightsPageRequested({ page: queryParams, id: this.data.id }));
    this.selection.clear();
  }

  createFormGroup() {
    // 'edit' prefix - for item editing
    // 'add' prefix - for item creation
    this.formGroup = this.fb.group({
      editWeight: ['', Validators.compose([Validators.required])],
      editStartDate: [
        this.typesUtilsService.getDateFromString(),
        Validators.compose([Validators.required]),
      ],
      newWeight: ['', Validators.compose([Validators.required])],
      newStartDate: [
        this.typesUtilsService.getDateFromString(),
        Validators.compose([Validators.required]),
      ],
    });
    this.clearAddForm();
    this.clearEditForm();
  }

  // ADD Weight FUNCTIONS: clearAddForm | checkAddForm | addWeightButtonOnClick | cancelAddButtonOnClick | saveNewWeight
  clearAddForm() {
    const controls = this.formGroup.controls;
    controls.newWeight.setValue('');
    controls.newWeight.markAsPristine();
    controls.newWeight.markAsUntouched();
    controls.newStartDate.setValue(this.typesUtilsService.getDateFromString());
    controls.newStartDate.markAsPristine();
    controls.newStartDate.markAsUntouched();

    this.weightForAdd = new PatientWeightModel();
    this.weightForAdd.clear(this.patientId);
    this.weightForAdd.start_date = this.typesUtilsService.getDateStringFromDate();
    this.weightForAdd._isEditMode = false;
  }

  /**
   * Check if Add Form is Valid
   */
  checkAddForm() {
    const controls = this.formGroup.controls;
    if (controls.newWeight.invalid || controls.newStartDate.invalid) {
      controls.newWeight.markAsTouched();
      controls.newStartDate.markAsTouched();
      return false;
    }

    return true;
  }

  /**
   * Close Remark Add Form
   */
  cancelAddButtonOnClick() {
    this.weightForAdd._isEditMode = false;
    this.isSwitchedToEditMode = false;
  }

  /**
   *  Create new Weight
   */
  saveNewWeight() {
    if (!this.checkAddForm()) {
      return;
    }

    const controls = this.formGroup.controls;
    this.loadingAfterSubmit = false;
    this.weightForAdd._isEditMode = false;
    this.weightForAdd.weight =
      this.measurementSystem === 'm'
        ? controls.newWeight.value * 1000
        : controls.newWeight.value * 454;
    const _date = new Date(controls.newStartDate.value);
    this.weightForAdd.start_date = this.datePipe.transform(_date, 'yyyy-MM-dd');
    this.weightForAdd._createdDate = this.weightForEdit._updatedDate;
    this.store.dispatch(
      new PatientWeightOnServerCreated({ patientWeight: this.weightForAdd, id: this.patientId }),
    );

    const actionLoadingSubscription = this.store
      .pipe(select(selectPatientWeightsActionLoading))
      .subscribe((res) => {
        if (res !== true) {
          const errorStateSubscription = this.store
            .pipe(select(selectPatientWeightErrorState))
            .subscribe((error) => {
              if (error) {
                if (error.status) {
                  this.submissionErrorObj = error.error.errors;
                } else {
                  this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                }
                this.hasSubmissionErrors = true;
              } else {
                this.submissionErrorObj = null;
                this.hasSubmissionErrors = false;
                const message = this.translate.instant('PATIENT.PATIENT_WEIGHT.EDIT.ADD_MESSAGE');
                this.layoutUtilsService.showActionNotification(
                  message,
                  MessageType.Update,
                  10000,
                  true,
                  true,
                );
                this.isSwitchedToEditMode = false;
                this.clearAddForm();
              }
              this.cdr.detectChanges();
            });
          this.subscriptions.push({ id: 5, sub: errorStateSubscription });
        }
        this.cdr.detectChanges();
      });
    this.subscriptions.push({ id: 4, sub: actionLoadingSubscription });
  }

  // EDIT Weight FUNCTIONS: clearEditForm | checkEditForm | editWeightButtonOnClick | cancelEditButtonOnClick |
  clearEditForm() {
    const controls = this.formGroup.controls;
    controls.editWeight.setValue('');
    controls.editStartDate.setValue(this.typesUtilsService.getDateFromString());

    this.weightForEdit = new PatientWeightModel();
    this.weightForEdit.clear(this.patientId);
    this.weightForEdit.start_date = this.typesUtilsService.getDateStringFromDate();
    this.weightForEdit._isEditMode = false;
  }

  /**
   * Open Weight Add Form
   */
  addWeightButtonOnClick() {
    this.clearAddForm();
    this.weightForAdd._isEditMode = true;
    this.isSwitchedToEditMode = true;
    this.cdr.detectChanges();
  }

  /**
   * Returns object for filter
   */
  filterConfiguration(): any {
    const filter: any = {};
    // const searchText: string = this.searchInput.nativeElement.value;
    filter.status = this.filterStatus;
    //filter.text = searchText ? searchText : '';

    // if (!searchText) {
    //   return filter;
    // }

    // filter.name = searchText;
    // filter.patient = searchText;
    return filter;
  }

  convertWeight(weight) {
    if (this.measurementSystem === 'm') {
      return (weight / 1000).toFixed(2);
    } else {
      return (weight / 454).toFixed(2);
    }
  }

  /**
   * Close dialog
   */
  closeDialog(): void {
    const sortedResult = this.patientWeightsResult.sort(function (a, b) {
      const x = a['start_date'];
      const y = b['start_date'];
      return x < y ? -1 : x > y ? 1 : 0;
    });
    this.dialogRef.close({
      weight: sortedResult.length > 0 ? sortedResult[sortedResult.length - 1].weight : 0,
    });
  }

  getWeightTitle() {
    return this.measurementSystem === 'm'
      ? 'PATIENT.PATIENT_WEIGHT.TABLE.WEIGHT_KG'
      : 'PATIENT.PATIENT_WEIGHT.TABLE.WEIGHT_LB';
  }
}
